import React from 'react';
import ColorSwatch from '../../ui/ColorSwatch';
import PlotTypeSelector from '../../ui/PlotTypeSelector';
import { UseMapVisualizationReturn, ScatterShape, TrajectoryStyle } from '../../../hooks/useMapVisualization';
import { StatType } from '../../../types/database';
import { VisualizationEvent } from '../types';

interface ColorStyleControlsProps {
  mapVisualization: UseMapVisualizationReturn;
  statTypes: StatType[];
  events: VisualizationEvent[];
  homeTeamId: string;
  awayTeamId: string;
  homeTeamName?: string;
  awayTeamName?: string;
  compareMode?: boolean;
}

/**
 * ColorStyleControls - Legend component with color pickers and shape/style selectors
 * Extracted from MapsTab to be reusable across components
 */
const ColorStyleControls: React.FC<ColorStyleControlsProps> = ({
  mapVisualization,
  statTypes,
  events,
  homeTeamId,
  awayTeamId,
  homeTeamName = 'Home Team',
  awayTeamName = 'Away Team',
  compareMode = false,
}) => {
  const {
    colorBy,
    teamColors,
    setTeamColors,
    setStatTypeColors,
    scatterShapes,
    setScatterShapes,
    trajectoryStyles,
    setTrajectoryStyles,
    getStatTypeColor,
    getVisualizationTypeForItem,
  } = mapVisualization;

  // Compute hasFilteredData from actual events passed to component
  const hasFilteredData = events && events.length > 0;

  // Helper function to render a single stat type item for a specific team
  const renderStatTypeItem = (statTypeId: string, teamId: string, align: 'left' | 'right') => {
    const statType = statTypes.find((st) => st.id === statTypeId);
    const statTypeName = statType?.display_name || statType?.short_description || statType?.stat_key || 'Unknown';
    const vizType = getVisualizationTypeForItem(statTypeId, true);
    const statTypeColor = getStatTypeColor(statTypeId, teamId);
    const teamName = teamId === homeTeamId ? homeTeamName : teamId === awayTeamId ? awayTeamName : 'Unknown Team';
    const colorKey = `${statTypeId}-${teamId}`;
    
    return (
      <div className="flex items-center space-x-2 py-1">
        {align === 'right' ? (
          // Right aligned: Text first, then controls (mirrored order)
          <>
            <span className="text-xs text-gray-600 truncate" title={`${statTypeName} (${teamName})`}>
              {statTypeName}
            </span>
            <div className="flex items-center space-x-2">
              <ColorSwatch
                color={statTypeColor}
                onChange={(newColor) => {
                  setStatTypeColors((prev) => ({
                    ...prev,
                    [colorKey]: newColor,
                  }));
                }}
              />
              {/* Reverse order for mirror effect: trajectory first, then scatter */}
              {vizType === 'trajectory' || vizType === 'both' ? (
                <PlotTypeSelector
                  type="trajectory"
                  currentValue={trajectoryStyles[colorKey] || trajectoryStyles[statTypeId] || 'progressive'}
                  onChange={(style) => {
                    setTrajectoryStyles((prev) => ({
                      ...prev,
                      [colorKey]: style as TrajectoryStyle,
                    }));
                  }}
                  color={statTypeColor}
                />
              ) : null}
              {vizType === 'scatter' || vizType === 'both' ? (
                <PlotTypeSelector
                  type="scatter"
                  currentValue={scatterShapes[colorKey] || scatterShapes[statTypeId] || 'circle'}
                  onChange={(shape) => {
                    setScatterShapes((prev) => ({
                      ...prev,
                      [colorKey]: shape as ScatterShape,
                    }));
                  }}
                  color={statTypeColor}
                />
              ) : null}
            </div>
          </>
        ) : (
          // Left aligned: Controls first, then text
          <>
            <div className="flex items-center space-x-2">
              {vizType === 'scatter' || vizType === 'both' ? (
                <PlotTypeSelector
                  type="scatter"
                  currentValue={scatterShapes[colorKey] || scatterShapes[statTypeId] || 'circle'}
                  onChange={(shape) => {
                    setScatterShapes((prev) => ({
                      ...prev,
                      [colorKey]: shape as ScatterShape,
                    }));
                  }}
                  color={statTypeColor}
                />
              ) : null}
              {vizType === 'trajectory' || vizType === 'both' ? (
                <PlotTypeSelector
                  type="trajectory"
                  currentValue={trajectoryStyles[colorKey] || trajectoryStyles[statTypeId] || 'progressive'}
                  onChange={(style) => {
                    setTrajectoryStyles((prev) => ({
                      ...prev,
                      [colorKey]: style as TrajectoryStyle,
                    }));
                  }}
                  color={statTypeColor}
                />
              ) : null}
              <ColorSwatch
                color={statTypeColor}
                onChange={(newColor) => {
                  setStatTypeColors((prev) => ({
                    ...prev,
                    [colorKey]: newColor,
                  }));
                }}
              />
            </div>
            <span className="text-xs text-gray-600 truncate" title={`${statTypeName} (${teamName})`}>
              {statTypeName}
            </span>
          </>
        )}
      </div>
    );
  };

  // Helper function to render legend items (without wrapper) for a specific team or all teams
  const renderLegendItems = (teamId?: string, align: 'left' | 'right' = 'left') => {
    if (!hasFilteredData) return null;

    // Get unique teams from events
    const teamSet = new Set<string>();
    events.forEach((event) => {
      if (event.team_id) teamSet.add(event.team_id);
    });
    const uniqueTeams = Array.from(teamSet);

    // Get unique stat types from events
    const statTypeSet = new Set<string>();
    events.forEach((event) => {
      if (event.stat_type_id) statTypeSet.add(event.stat_type_id);
    });
    const uniqueStatTypes = Array.from(statTypeSet);

    // Filter teams/stat types based on teamId if provided
    const teamsToShow = teamId ? uniqueTeams.filter(t => t === teamId) : uniqueTeams;
    
    // For stat_type colorBy mode, when teamId is not provided but align is specified,
    // show stat types for the team corresponding to that alignment
    let statTypesToShow: string[];
    let effectiveTeamId: string | undefined = teamId;
    
    if (colorBy === 'stat_type' && !teamId) {
      // In single view mode with stat_type colorBy, show stat types per team based on alignment
      if (align === 'left') {
        effectiveTeamId = homeTeamId;
        statTypesToShow = uniqueStatTypes; // Show all stat types, will filter by presence in rendering
      } else if (align === 'right') {
        effectiveTeamId = awayTeamId;
        statTypesToShow = uniqueStatTypes; // Show all stat types, will filter by presence in rendering
      } else {
        statTypesToShow = uniqueStatTypes;
      }
    } else if (colorBy === 'stat_type' && teamId) {
      // In compare mode with stat_type colorBy, show ALL stat types (not just for this team)
      // This ensures alignment across both legends
      statTypesToShow = uniqueStatTypes;
    } else {
      statTypesToShow = teamId 
        ? uniqueStatTypes.filter(st => events.some(e => e.stat_type_id === st && e.team_id === teamId))
        : uniqueStatTypes;
    }

    return (
      <div className={`flex flex-row flex-wrap gap-x-4 gap-y-1.5 ${align === 'right' ? 'justify-end' : 'justify-start'}`}>
        {colorBy === 'team' && teamsToShow.length > 0 && (
          <>
            {teamsToShow.map((tId) => {
              const teamName = tId === homeTeamId ? homeTeamName : tId === awayTeamId ? awayTeamName : 'Unknown Team';
              const teamColor = teamColors[tId] || '#3B82F6';
              const vizType = getVisualizationTypeForItem(tId, false);
              
              return (
                <div key={tId} className="flex items-center space-x-2 py-1">
                  {align === 'right' ? (
                    // Right aligned: Text first, then controls (mirrored order)
                    <>
                      <span className="text-xs text-gray-600 truncate">{teamName}</span>
                      <div className="flex items-center space-x-2">
                        <ColorSwatch
                          color={teamColor}
                          onChange={(newColor) => {
                            setTeamColors((prev) => ({
                              ...prev,
                              [tId]: newColor,
                            }));
                          }}
                        />
                        {/* Reverse order for mirror effect: trajectory first, then scatter */}
                        {vizType === 'trajectory' || vizType === 'both' ? (
                          <PlotTypeSelector
                            type="trajectory"
                            currentValue={trajectoryStyles[tId] || 'progressive'}
                            onChange={(style) => {
                              setTrajectoryStyles((prev) => ({
                                ...prev,
                                [tId]: style as TrajectoryStyle,
                              }));
                            }}
                            color={teamColor}
                          />
                        ) : null}
                        {vizType === 'scatter' || vizType === 'both' ? (
                          <PlotTypeSelector
                            type="scatter"
                            currentValue={scatterShapes[tId] || 'circle'}
                            onChange={(shape) => {
                              setScatterShapes((prev) => ({
                                ...prev,
                                [tId]: shape as ScatterShape,
                              }));
                            }}
                            color={teamColor}
                          />
                        ) : null}
                      </div>
                    </>
                  ) : (
                    // Left aligned: Controls first, then text
                    <>
                      <div className="flex items-center space-x-2">
                        {vizType === 'scatter' || vizType === 'both' ? (
                          <PlotTypeSelector
                            type="scatter"
                            currentValue={scatterShapes[tId] || 'circle'}
                            onChange={(shape) => {
                              setScatterShapes((prev) => ({
                                ...prev,
                                [tId]: shape as ScatterShape,
                              }));
                            }}
                            color={teamColor}
                          />
                        ) : null}
                        {vizType === 'trajectory' || vizType === 'both' ? (
                          <PlotTypeSelector
                            type="trajectory"
                            currentValue={trajectoryStyles[tId] || 'progressive'}
                            onChange={(style) => {
                              setTrajectoryStyles((prev) => ({
                                ...prev,
                                [tId]: style as TrajectoryStyle,
                              }));
                            }}
                            color={teamColor}
                          />
                        ) : null}
                        <ColorSwatch
                          color={teamColor}
                          onChange={(newColor) => {
                            setTeamColors((prev) => ({
                              ...prev,
                              [tId]: newColor,
                            }));
                          }}
                        />
                      </div>
                      <span className="text-xs text-gray-600 truncate">{teamName}</span>
                    </>
                  )}
                </div>
              );
            })}
          </>
        )}
        
        {colorBy === 'stat_type' && statTypesToShow.length > 0 && (
          <>
            {(() => {
              // Sort stat types alphabetically for consistent ordering
              const sortedStatTypes = Array.from(statTypesToShow).sort((a, b) => {
                const statA = statTypes.find((st) => st.id === a);
                const statB = statTypes.find((st) => st.id === b);
                const nameA = statA?.display_name || statA?.short_description || statA?.stat_key || '';
                const nameB = statB?.display_name || statB?.short_description || statB?.stat_key || '';
                return nameA.localeCompare(nameB);
              });
              
              return sortedStatTypes.map((statTypeId) => {
                // When teamId or effectiveTeamId is provided, show stat type for that specific team
                // Otherwise, show stat type for all teams (backward compatibility)
                if (effectiveTeamId) {
                  // Check if this stat type exists for the effective team
                  const hasStatForTeam = events.some(e => e.stat_type_id === statTypeId && e.team_id === effectiveTeamId);
                  
                  if (!hasStatForTeam) {
                    // If stat doesn't exist for this team, render empty placeholder to maintain alignment
                    return (
                      <div key={`${statTypeId}-${effectiveTeamId}-placeholder`} className="flex items-center space-x-2 py-1">
                        {/* Empty placeholder to maintain vertical alignment */}
                      </div>
                    );
                  }
                  
                  // Stat exists for this team, render it
                  return renderStatTypeItem(statTypeId, effectiveTeamId, align);
                } else {
                  // Show stat type for all teams (backward compatibility - when no teamId specified)
                  const statType = statTypes.find((st) => st.id === statTypeId);
                  const statTypeName = statType?.display_name || statType?.short_description || statType?.stat_key || 'Unknown';
                  const vizType = getVisualizationTypeForItem(statTypeId, true);
                  const statTypeColor = getStatTypeColor(statTypeId);
                  
                  return (
                    <div key={statTypeId} className="flex items-center space-x-2 py-1">
                      {align === 'right' ? (
                        // Right aligned: Text first, then controls
                        <>
                          <span className="text-xs text-gray-600 truncate" title={statTypeName}>
                            {statTypeName}
                          </span>
                          <div className="flex items-center space-x-2">
                            <ColorSwatch
                              color={statTypeColor}
                              onChange={(newColor) => {
                                setStatTypeColors((prev) => ({
                                  ...prev,
                                  [statTypeId]: newColor,
                                }));
                              }}
                            />
                            {vizType === 'scatter' || vizType === 'both' ? (
                              <PlotTypeSelector
                                type="scatter"
                                currentValue={scatterShapes[statTypeId] || 'circle'}
                                onChange={(shape) => {
                                  setScatterShapes((prev) => ({
                                    ...prev,
                                    [statTypeId]: shape as ScatterShape,
                                  }));
                                }}
                                color={statTypeColor}
                              />
                            ) : null}
                            {vizType === 'trajectory' || vizType === 'both' ? (
                              <PlotTypeSelector
                                type="trajectory"
                                currentValue={trajectoryStyles[statTypeId] || 'solid'}
                                onChange={(style) => {
                                  setTrajectoryStyles((prev) => ({
                                    ...prev,
                                    [statTypeId]: style as TrajectoryStyle,
                                  }));
                                }}
                                color={statTypeColor}
                              />
                            ) : null}
                          </div>
                        </>
                      ) : (
                        // Left aligned: Controls first, then text
                        <>
                          <div className="flex items-center space-x-2">
                            {vizType === 'scatter' || vizType === 'both' ? (
                              <PlotTypeSelector
                                type="scatter"
                                currentValue={scatterShapes[statTypeId] || 'circle'}
                                onChange={(shape) => {
                                  setScatterShapes((prev) => ({
                                    ...prev,
                                    [statTypeId]: shape as ScatterShape,
                                  }));
                                }}
                                color={statTypeColor}
                              />
                            ) : null}
                            {vizType === 'trajectory' || vizType === 'both' ? (
                              <PlotTypeSelector
                                type="trajectory"
                                currentValue={trajectoryStyles[statTypeId] || 'solid'}
                                onChange={(style) => {
                                  setTrajectoryStyles((prev) => ({
                                    ...prev,
                                    [statTypeId]: style as TrajectoryStyle,
                                  }));
                                }}
                                color={statTypeColor}
                              />
                            ) : null}
                            <ColorSwatch
                              color={statTypeColor}
                              onChange={(newColor) => {
                                setStatTypeColors((prev) => ({
                                  ...prev,
                                  [statTypeId]: newColor,
                                }));
                              }}
                            />
                          </div>
                          <span className="text-xs text-gray-600 truncate" title={statTypeName}>
                            {statTypeName}
                          </span>
                        </>
                      )}
                    </div>
                  );
                }
              });
            })()}
          </>
        )}
      </div>
    );
  };

  // Don't render if no filtered data
  if (!hasFilteredData) return null;

  // Render legend based on compare mode and colorBy
  if (compareMode) {
    return (
      <div className="mt-4 w-full">
        <h3 className="text-sm font-semibold text-gray-900 mb-2 text-center">Legend</h3>
        {colorBy === 'team' ? (
          <div className="flex flex-row flex-wrap gap-x-4 gap-y-1.5 justify-between items-start">
            {/* Home Team - Left aligned */}
            <div className="flex flex-row flex-wrap gap-x-4 gap-y-1.5 justify-start">
              {renderLegendItems(homeTeamId, 'left')}
            </div>
            {/* Away Team - Right aligned */}
            <div className="flex flex-row flex-wrap gap-x-4 gap-y-1.5 justify-end">
              {renderLegendItems(awayTeamId, 'right')}
            </div>
          </div>
        ) : colorBy === 'stat_type' ? (
          // For stat_type: Show each stat on the same line for both teams
          <div className="flex flex-col gap-y-1.5">
            {(() => {
              // Get all unique stat types that exist for either team
              const allStatTypes = new Set<string>();
              events.forEach((event) => {
                if (event.stat_type_id) allStatTypes.add(event.stat_type_id);
              });
              const sortedStatTypes = Array.from(allStatTypes).sort((a, b) => {
                const statA = statTypes.find((st) => st.id === a);
                const statB = statTypes.find((st) => st.id === b);
                const nameA = statA?.display_name || statA?.short_description || statA?.stat_key || '';
                const nameB = statB?.display_name || statB?.short_description || statB?.stat_key || '';
                return nameA.localeCompare(nameB);
              });
              
              return sortedStatTypes.map((statTypeId) => {
                const hasHomeTeam = events.some(e => e.stat_type_id === statTypeId && e.team_id === homeTeamId);
                const hasAwayTeam = events.some(e => e.stat_type_id === statTypeId && e.team_id === awayTeamId);
                
                return (
                  <div key={statTypeId} className="flex flex-row justify-between items-center gap-x-4">
                    {/* Home Team - Left aligned */}
                    <div className="flex justify-start flex-1">
                      {hasHomeTeam ? renderStatTypeItem(statTypeId, homeTeamId, 'left') : <div className="py-1" />}
                    </div>
                    {/* Away Team - Right aligned */}
                    <div className="flex justify-end flex-1">
                      {hasAwayTeam ? renderStatTypeItem(statTypeId, awayTeamId, 'right') : <div className="py-1" />}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        ) : null}
      </div>
    );
  } else {
    // Single view mode - match compare mode layout
    return (
      <div className="mt-4 w-full">
        <h3 className="text-sm font-semibold text-gray-900 mb-2 text-center">Legend</h3>
        {colorBy === 'team' ? (
          <div className="flex flex-row flex-wrap gap-x-4 gap-y-1.5 justify-between items-start">
            {/* Home Team - Left aligned */}
            <div className="flex flex-row flex-wrap gap-x-4 gap-y-1.5 justify-start">
              {renderLegendItems(homeTeamId, 'left')}
            </div>
            {/* Away Team - Right aligned */}
            <div className="flex flex-row flex-wrap gap-x-4 gap-y-1.5 justify-end">
              {renderLegendItems(awayTeamId, 'right')}
            </div>
          </div>
        ) : colorBy === 'stat_type' ? (
          // For stat_type: Show each stat on the same line for both teams (one stat per line)
          <div className="flex flex-col gap-y-1.5">
            {(() => {
              // Get all unique stat types that exist for either team
              const allStatTypes = new Set<string>();
              events.forEach((event) => {
                if (event.stat_type_id) allStatTypes.add(event.stat_type_id);
              });
              const sortedStatTypes = Array.from(allStatTypes).sort((a, b) => {
                const statA = statTypes.find((st) => st.id === a);
                const statB = statTypes.find((st) => st.id === b);
                const nameA = statA?.display_name || statA?.short_description || statA?.stat_key || '';
                const nameB = statB?.display_name || statB?.short_description || statB?.stat_key || '';
                return nameA.localeCompare(nameB);
              });
              
              return sortedStatTypes.map((statTypeId) => {
                const hasHomeTeam = events.some(e => e.stat_type_id === statTypeId && e.team_id === homeTeamId);
                const hasAwayTeam = events.some(e => e.stat_type_id === statTypeId && e.team_id === awayTeamId);
                
                // Skip line if neither team has this stat
                if (!hasHomeTeam && !hasAwayTeam) {
                  return null;
                }
                
                return (
                  <div key={statTypeId} className="flex flex-row justify-between items-center gap-x-4">
                    {/* Home Team - Left aligned */}
                    <div className="flex justify-start flex-1">
                      {hasHomeTeam ? renderStatTypeItem(statTypeId, homeTeamId, 'left') : <div className="py-1" />}
                    </div>
                    {/* Away Team - Right aligned */}
                    <div className="flex justify-end flex-1">
                      {hasAwayTeam ? renderStatTypeItem(statTypeId, awayTeamId, 'right') : <div className="py-1" />}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        ) : null}
      </div>
    );
  }
};

export default ColorStyleControls;

